<template>
  <div>
    <!-- Instructor Feedback (always show if present) -->
    <div v-if="quizResult && quizResult.completed && quizResult.feedback" class="mt-2 mb-6">
      <h4>You received the following feedback on your latest submission:</h4>
      <p>{{ quizResult.feedback }}</p>
    </div>

    <!-- Quiz Results (only show if feedback mode is enabled) -->
    <div v-if="quizResult && quizResult.completed && quizResult.quizFeedbackMode !== null && quizResult.quizFeedbackMode !== QuizFeedbackMode.NONE" class="mb-6">
      <div class="flex items-center justify-between mb-5">
        <h4 class="text-xl font-bold">Quiz Results</h4>
        <div class="flex items-center gap-2">
          <label class="flex items-center cursor-pointer">
            <input type="checkbox" v-model="showOnlyIncorrect" class="mr-2">
            <span class="text-sm font-medium text-gray-700">Show only incorrect answers</span>
          </label>
        </div>
      </div>
      <div v-if="filteredQuestionAnswers.length === 0 && showOnlyIncorrect" class="bg-green-50 border-l-4 border-green-500 p-6 rounded shadow-sm text-center">
        <d-icon icon="check-circle" class="text-green-500 text-4xl mb-3" />
        <h5 class="text-lg font-bold text-green-800 mb-2">Perfect Score!</h5>
        <p class="text-green-800">You answered all questions correctly. Great job!</p>
      </div>
      <div v-else class="space-y-3 max-h-96 overflow-y-auto pr-2">
        <div 
          v-for="(answer, index) in filteredQuestionAnswers" 
          :key="answer.quizQuestionId" 
          class="border-l-4 bg-white rounded shadow-sm"
          :class="answer.isCorrect ? 'border-green-500' : 'border-red-600'"
        >
          <div class="p-4">
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm font-bold text-gray-900 uppercase tracking-wide">Question {{ answer.originalIndex + 1 }}</span>
              <span 
                v-if="quizResult.quizFeedbackMode === QuizFeedbackMode.INDICATE_CORRECTNESS || quizResult.quizFeedbackMode === QuizFeedbackMode.SHOW_CORRECT_ANSWERS" 
                class="flex items-center font-semibold text-sm px-2 py-1 rounded"
                :class="answer.isCorrect ? 'text-green-800 bg-green-50' : 'text-red-700 bg-red-50'"
              >
                <d-icon :icon="answer.isCorrect ? 'check-circle' : 'times-circle'" class="mr-1.5" />
                {{ answer.isCorrect ? 'Correct' : 'Incorrect' }}
              </span>
            </div>
            
            <div class="mb-3 text-gray-900 leading-relaxed" v-html="answer.content"></div>
            
            <div class="text-sm space-y-2">
              <div v-if="getQuestionChoices(answer.quizQuestionId).length > 0" class="mb-2">
                <p class="font-semibold text-gray-800 mb-1">Answer Choices:</p>
                <ul class="list-disc list-inside text-gray-700 pl-2">
                  <li 
                    v-for="choice in getQuestionChoices(answer.quizQuestionId)" 
                    :key="choice.id" 
                    :class="quizResult.quizFeedbackMode === QuizFeedbackMode.SHOW_CORRECT_ANSWERS && isChoiceCorrect(choice, answer) ? 'text-green-800 font-semibold' : 'text-gray-800'"
                  >
                    {{ choice.content }}
                    <span v-if="quizResult.quizFeedbackMode === QuizFeedbackMode.SHOW_CORRECT_ANSWERS && isChoiceCorrect(choice, answer)" class="text-green-700 ml-2">✓ Correct</span>
                  </li>
                </ul>
              </div>
              
              <div class="mb-2">
                <p class="font-semibold text-gray-800">Your answer:</p>
                <ul v-if="formatAnswerList(answer.selectedAnswer).length > 1" class="list-disc list-inside text-gray-900 pl-2 mt-1">
                  <li v-for="(ans, idx) in formatAnswerList(answer.selectedAnswer)" :key="idx">{{ ans }}</li>
                </ul>
                <p v-else class="text-gray-900 pl-2">{{ answer.selectedAnswer || 'Not answered' }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { QuizFeedbackMode } from '@resources/scripts/constants';

export default {
  props: {
    quizResult: {
      type: Object,
      required: true
    },
    quizQuestions: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const showOnlyIncorrect = ref(false);

    // Create a map of question ID to answer choices for efficient lookup
    const questionChoicesMap = computed(() => {
      const map = new Map();
      props.quizQuestions.forEach(question => {
        map.set(question.id, question.answerChoices || []);
      });
      return map;
    });

    const getQuestionChoices = (quizQuestionId) => {
      return questionChoicesMap.value.get(quizQuestionId) || [];
    };

    const isChoiceCorrect = (choice, answer) => {
      if (!answer.correctAnswerText) return false;
      
      // Split by "<<SPLIT>>" to handle multiple correct answers
      const correctAnswers = answer.correctAnswerText.split('<<SPLIT>>');
      return correctAnswers.includes(choice.content);
    };

    const formatAnswerList = (answerText) => {
      if (!answerText) return [];
      // Split by "<<SPLIT>>" to get individual answers
      return answerText.split('<<SPLIT>>');
    };

    const filteredQuestionAnswers = computed(() => {
      if (!props.quizResult?.questionAnswers) return [];
      
      const answersWithIndex = props.quizResult.questionAnswers.map((answer, index) => ({
        ...answer,
        originalIndex: index
      }));

      if (showOnlyIncorrect.value) {
        return answersWithIndex.filter(answer => !answer.isCorrect);
      }
      return answersWithIndex;
    });

    return {
      QuizFeedbackMode,
      getQuestionChoices,
      showOnlyIncorrect,
      filteredQuestionAnswers,
      isChoiceCorrect,
      formatAnswerList
    };
  }
};
</script>
